import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Upload, RefreshCw, Plus, Trash2, FileText, Download,
  ChevronLeft, ChevronRight, TrendingUp, AlertTriangle, Plug, CheckCircle2,
  Sparkles, Database,
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import { API } from '../../App';
import { useAlert } from '../../context/AlertContext';
import MrvKpiCard from '../../components/mrv/MrvKpiCard';
import useLegendToggle from '../../utils/useLegendToggle';

/**
 * MRV Activity Data panel
 *
 * - Reads `/api/mrv/activity-data` (mrv_v_activity_data view → native + OIM rows)
 * - Server-side pagination (offset / limit / total)
 * - Manual entry, soft-delete (native only), CSV upload
 * - Sparkline of daily volume split by data origin (native vs OIM-streamed)
 */

// Aligns with OIM's energy_type / water_type / waste_type / product columns —
// no _consumption suffix; just pass-through. See backend mrv_emissions.SCOPE_MAP.
const ACTIVITY_TYPES = [
  // OIM energy_type pass-through (matches oim_energy_hourly.energy_type)
  'electricity', 'renewable',
  'diesel', 'petrol', 'furnace_oil', 'natural_gas', 'lpg', 'coal',
  'bagasse', 'biomass', 'husk', 'wood',
  'steam', 'fuel',
  // Rolled-up MRV categories
  'water',      // OIM water_hourly → all water_types roll up
  'waste',      // OIM waste_hourly → all waste_types roll up
  'production', // OIM production_hourly → all products roll up
  'distance',   // value-chain transport
];

const PAGE_SIZE_OPTIONS = [25, 50, 100, 250];

// Color palette for activity-type series (stacked area + donut)
const ACTIVITY_COLORS = [
  '#2563eb', // blue
  '#16a34a', // green
  '#d97706', // orange
  '#7c3aed', // violet
  '#dc2626', // red
  '#0891b2', // cyan
  '#db2777', // pink
];

// Surface the data_origin column as a neutral category label.
const CATEGORY_LABEL = {
  mrv_native: 'Manual entry',
  oim_energy_hourly: 'Energy',
  oim_production_hourly: 'Production',
  oim_water_hourly: 'Water',
  oim_waste_hourly: 'Waste',
};

const TEMPLATE_CSV =
  'timestamp,site_id,activity_type,activity_value,unit,asset_id,notes\n' +
  '2026-05-01T00:00:00Z,site-uuid,electricity_consumption,12500,kWh,,Manual entry — Apr meter reading\n';

export default function ActivityPanel({ siteId }) {
  const { showSuccess, showError, showInfo } = useAlert();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({ activity_type: '', data_origin: '', start: '', end: '' });
  const [page, setPage] = useState(0);          // 0-indexed
  const [pageSize, setPageSize] = useState(50);
  const [showCreate, setShowCreate] = useState(false);
  const [volume, setVolume] = useState([]);      // daily activity-volume series
  const [ingestion, setIngestion] = useState(null);  // M1 dashboard payload
  const fileInputRef = useRef(null);
  // Click-to-toggle legend state — one per chart in this panel.
  const volumeLegend = useLegendToggle();
  const distLegend = useLegendToggle();

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const offset = page * pageSize;

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (siteId) params.set('site_id', siteId);
      if (filter.activity_type) params.set('activity_type', filter.activity_type);
      if (filter.data_origin) params.set('data_origin', filter.data_origin);
      if (filter.start) params.set('start', filter.start);
      if (filter.end) params.set('end', filter.end);
      params.set('offset', String(offset));
      params.set('limit', String(pageSize));

      const res = await axios.get(`${API}/mrv/activity-data?${params}`);
      setRows(res.data.activity_data || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      console.error(e);
      showError(e.response?.data?.detail || 'Failed to load activity data');
    } finally {
      setLoading(false);
    }
  }, [siteId, filter, offset, pageSize, showError]);

  // Volume chart pulls the full sparkline from /dashboard/stats once
  const fetchVolume = useCallback(async () => {
    try {
      const qs = siteId ? `?site_id=${siteId}` : '';
      const res = await axios.get(`${API}/mrv/dashboard/stats${qs}`);
      setVolume(res.data?.activity_volume || []);
    } catch (e) {
      // Non-fatal
      console.warn('Volume fetch failed', e);
    }
  }, [siteId]);

  // M1 ingestion dashboard (KPI cards + ingestion volume + activity distribution)
  const fetchIngestion = useCallback(async () => {
    try {
      const qs = siteId ? `?site_id=${siteId}` : '';
      const res = await axios.get(`${API}/mrv/ingestion/dashboard${qs}`);
      setIngestion(res.data);
    } catch (e) {
      console.warn('Ingestion dashboard fetch failed', e);
    }
  }, [siteId]);

  useEffect(() => { fetchRows(); }, [fetchRows]);
  useEffect(() => { fetchVolume(); fetchIngestion(); }, [fetchVolume, fetchIngestion]);

  // Reset page when filters change
  useEffect(() => { setPage(0); }, [filter, pageSize]);

  const handleDelete = async (id) => {
    if (!window.confirm('Soft-delete this activity row?')) return;
    try {
      await axios.delete(`${API}/mrv/activity-data/${id}`);
      showSuccess('Activity row deleted');
      fetchRows();
    } catch (e) {
      showError(e.response?.data?.detail || 'Delete failed');
    }
  };

  const handleUpload = async (file) => {
    if (!file) return;
    if (!siteId) {
      showError('Select a site first — every row will be attributed to it');
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    try {
      const params = new URLSearchParams({ site_id: siteId });
      const res = await axios.post(
        `${API}/mrv/upload/import?${params}`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const d = res.data;
      if (d.rows_failed === 0) {
        showSuccess(`Imported ${d.rows_imported} of ${d.rows_total} rows`);
      } else {
        showInfo(`Imported ${d.rows_imported} of ${d.rows_total} rows (${d.rows_failed} failed — see console)`);
        console.warn('Import errors:', d.errors);
      }
      setPage(0);
      fetchRows();
      fetchVolume();
    } catch (e) {
      showError(e.response?.data?.detail || 'Upload failed');
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE_CSV], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'mrv_activity_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const showingFrom = total === 0 ? 0 : offset + 1;
  const showingTo = Math.min(offset + rows.length, total);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Data Ingestion Dashboard</h2>
          <div className="text-xs text-gray-500">M1 — Module 1 · Data Ingestion Engine</div>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={downloadTemplate}
            className="px-3 py-2 text-sm font-medium text-blue-700 hover:text-blue-900 hover:bg-blue-50 rounded-md inline-flex items-center gap-1"
          >
            <Download className="w-4 h-4" /> Template
          </button>
          <input ref={fileInputRef} type="file" accept=".csv,text/csv"
                 onChange={e => handleUpload(e.target.files?.[0])} className="hidden" />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-md text-sm font-medium inline-flex items-center gap-1"
          >
            <Upload className="w-4 h-4" /> Upload CSV
          </button>
          <button
            type="button"
            onClick={() => setShowCreate(s => !s)}
            className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-sm font-medium inline-flex items-center gap-1"
          >
            <Plus className="w-4 h-4" /> Add row
          </button>
          <button
            type="button"
            onClick={() => { fetchRows(); fetchVolume(); }}
            disabled={loading}
            className="p-2 text-gray-500 hover:text-gray-700"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* M1 Dashboard — KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <MrvKpiCard
          title="Records Today"
          value={ingestion?.records_today != null ? Number(ingestion.records_today).toLocaleString() : '—'}
          sub={`${Number(ingestion?.total_records || 0).toLocaleString()} total records`}
          Icon={TrendingUp}
          accent="blue"
          loading={!ingestion && loading}
        />
        <MrvKpiCard
          title="Failed Records"
          value={ingestion?.failed_records != null ? Number(ingestion.failed_records).toLocaleString() : '—'}
          sub="Last 24 hours"
          Icon={AlertTriangle}
          accent={(ingestion?.failed_records || 0) > 0 ? 'red' : 'emerald'}
          loading={!ingestion && loading}
        />
        <MrvKpiCard
          title="Active Connectors"
          value={ingestion?.active_sources ?? '—'}
          sub="Data sources online"
          Icon={Plug}
          accent="emerald"
          loading={!ingestion && loading}
        />
        <MrvKpiCard
          title="Avg Quality Score"
          value={ingestion?.avg_quality_score != null ? `${ingestion.avg_quality_score}%` : '—'}
          sub="Across all records"
          Icon={CheckCircle2}
          accent="violet"
          loading={!ingestion && loading}
        />
      </div>

      {/* M1 — Ingestion Volume + Activity Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <div className="lg:col-span-2 bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-baseline justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-700">Ingestion Volume</h3>
            <span className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full font-medium">Live</span>
          </div>
          <div className="text-xs text-gray-500 mb-2">Last 7 days by activity type</div>
          {(ingestion?.ingestion_volume || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={ingestion.ingestion_volume}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                <XAxis
                  dataKey="day"
                  tick={{ fontSize: 11 }}
                  tickFormatter={v => v ? new Date(v).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''}
                />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip
                  labelFormatter={v => v ? new Date(v).toLocaleDateString() : ''}
                />
                <Legend
                  wrapperStyle={{ fontSize: 11 }}
                  onClick={volumeLegend.onLegendClick}
                  formatter={volumeLegend.legendFormatter}
                />
                {(ingestion.activity_types_seen || []).slice(0, 7).map((at, i) => (
                  <Area
                    key={at}
                    type="monotone"
                    dataKey={at}
                    stackId="1"
                    stroke={ACTIVITY_COLORS[i % ACTIVITY_COLORS.length]}
                    fill={ACTIVITY_COLORS[i % ACTIVITY_COLORS.length]}
                    fillOpacity={0.4}
                    name={at}
                    hide={volumeLegend.isHidden(at)}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-xs text-gray-400 text-center py-12">No ingestion data yet — upload a CSV to populate</div>
          )}
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Activity Distribution</h3>
          <div className="text-xs text-gray-500 mb-2">Last 30 days</div>
          {(ingestion?.activity_distribution || []).length > 0 ? (() => {
            // Keep all slices so the legend always lists every activity type;
            // hidden ones just have their value zeroed out so the Pie wedge
            // collapses to 0° without dropping the legend entry.
            const distAll = ingestion.activity_distribution.slice(0, 7);
            const renderData = distAll.map(d => ({
              ...d,
              row_count: distLegend.isHidden(d.activity_type) ? 0 : d.row_count,
            }));
            return (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={renderData}
                    dataKey="row_count"
                    nameKey="activity_type"
                    innerRadius={45}
                    outerRadius={75}
                  >
                    {renderData.map((_, i) => (
                      <Cell key={i} fill={ACTIVITY_COLORS[i % ACTIVITY_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={v => Number(v).toLocaleString() + ' rows'} />
                  <Legend
                    layout="vertical"
                    align="right"
                    verticalAlign="middle"
                    iconSize={8}
                    wrapperStyle={{ fontSize: 10 }}
                    onClick={(entry) => distLegend.onLegendClick({ value: entry.value })}
                    formatter={distLegend.legendFormatter}
                  />
                </PieChart>
              </ResponsiveContainer>
            );
          })() : (
            <div className="text-xs text-gray-400 text-center py-12">No activity yet</div>
          )}
        </div>
      </div>

      {/* Daily volume sparkline */}
      {volume.length > 0 && (
        <div className="mb-4 p-3 rounded-lg bg-white border border-gray-200">
          <div className="flex items-baseline justify-between mb-1">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Activity volume — last 30 days
            </div>
            <div className="text-xs text-gray-500">
              <span className="inline-block w-2 h-2 rounded bg-emerald-500 mr-1" /> Manual entry
              <span className="inline-block w-2 h-2 rounded bg-blue-500 ml-3 mr-1" /> Streamed
            </div>
          </div>
          <ResponsiveContainer width="100%" height={120}>
            <BarChart data={volume}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
              <XAxis
                dataKey="day"
                tick={{ fontSize: 10 }}
                tickFormatter={v => v ? new Date(v).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''}
                interval="preserveStartEnd"
              />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip
                labelFormatter={v => v ? new Date(v).toLocaleDateString() : ''}
                formatter={(val, name) => [val, name === 'native' ? 'Manual entry' : 'Streamed']}
              />
              <Bar dataKey="native" stackId="a" fill="#10b981" />
              <Bar dataKey="oim" stackId="a" fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {showCreate && (
        <CreateRowForm
          siteId={siteId}
          onCreated={() => { setShowCreate(false); setPage(0); fetchRows(); fetchVolume(); }}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {/* Activity Data section header */}
      <div className="mt-6 mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Database className="w-4 h-4 text-gray-500" />
          <h3 className="text-base font-semibold text-gray-900">Recent Activity Records</h3>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-3 text-sm items-center">
        <select
          value={filter.activity_type}
          onChange={e => setFilter(f => ({ ...f, activity_type: e.target.value }))}
          className="px-2 py-1 border rounded"
        >
          <option value="">All types</option>
          {ACTIVITY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select
          value={filter.data_origin}
          onChange={e => setFilter(f => ({ ...f, data_origin: e.target.value }))}
          className="px-2 py-1 border rounded"
        >
          <option value="">All categories</option>
          <option value="mrv_native">Manual entry</option>
          <option value="oim_energy_hourly">Energy</option>
          <option value="oim_production_hourly">Production</option>
          <option value="oim_water_hourly">Water</option>
          <option value="oim_waste_hourly">Waste</option>
        </select>
        <input
          type="datetime-local"
          value={filter.start}
          onChange={e => setFilter(f => ({ ...f, start: e.target.value }))}
          className="px-2 py-1 border rounded"
        />
        <input
          type="datetime-local"
          value={filter.end}
          onChange={e => setFilter(f => ({ ...f, end: e.target.value }))}
          className="px-2 py-1 border rounded"
        />
        <span className="ml-auto text-xs text-gray-600">
          Page size:
          <select
            value={pageSize}
            onChange={e => setPageSize(parseInt(e.target.value))}
            className="ml-1 px-1.5 py-0.5 border rounded text-xs"
          >
            {PAGE_SIZE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </span>
      </div>

      {/* Table */}
      <div className="overflow-x-auto border border-gray-200 rounded-md">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-left">
              <th className="px-3 py-2 font-semibold text-gray-700">Timestamp</th>
              <th className="px-3 py-2 font-semibold text-gray-700">Activity Type</th>
              <th className="px-3 py-2 font-semibold text-gray-700 text-right">Value</th>
              <th className="px-3 py-2 font-semibold text-gray-700">Unit</th>
              <th className="px-3 py-2 font-semibold text-gray-700">Scope</th>
              <th className="px-3 py-2 font-semibold text-gray-700 text-right">co2e (kg)</th>
              <th className="px-3 py-2 font-semibold text-gray-700">Category</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-gray-500">{loading ? 'Loading…' : 'No activity records'}</td></tr>
            )}
            {rows.map(r => (
              <tr key={r.id} className="hover:bg-gray-50">
                <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{r.timestamp ? new Date(r.timestamp).toISOString() : ''}</td>
                <td className="px-3 py-2 text-gray-900">{r.activity_type}</td>
                <td className="px-3 py-2 text-right font-mono">{Number(r.activity_value).toFixed(2)}</td>
                <td className="px-3 py-2 text-gray-600">{r.unit}</td>
                <td className="px-3 py-2">
                  {r.emission_scope ? (
                    <span className="px-2 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-700">{r.emission_scope}</span>
                  ) : <span className="text-gray-400">—</span>}
                </td>
                <td className="px-3 py-2 text-right font-mono text-emerald-700">
                  {r.co2e_kg != null ? Number(r.co2e_kg).toFixed(2) : '—'}
                </td>
                <td className="px-3 py-2 text-xs">
                  <span className={`px-1.5 py-0.5 rounded ${r.data_origin === 'mrv_native' ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700'}`}>
                    {CATEGORY_LABEL[r.data_origin] || r.data_origin || '—'}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  {r.data_origin === 'mrv_native' && (
                    <button
                      onClick={() => handleDelete(r.id)}
                      className="text-red-500 hover:text-red-700 p-1"
                      title="Soft-delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination footer */}
      <div className="mt-3 flex items-center justify-between text-xs text-gray-600">
        <div>
          {total > 0 ? (
            <>Showing <span className="font-mono">{showingFrom}</span>–<span className="font-mono">{showingTo}</span> of <span className="font-mono">{total.toLocaleString()}</span></>
          ) : (
            <>{loading ? 'Loading…' : 'No rows'}</>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setPage(0)}
            disabled={page === 0 || loading}
            className="px-2 py-1 border rounded text-xs hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            ‹‹
          </button>
          <button
            type="button"
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0 || loading}
            className="px-2 py-1 border rounded text-xs hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center"
          >
            <ChevronLeft className="w-3.5 h-3.5" /> Prev
          </button>
          <span className="px-2">
            Page <span className="font-mono">{page + 1}</span> / <span className="font-mono">{totalPages.toLocaleString()}</span>
          </span>
          <button
            type="button"
            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1 || loading}
            className="px-2 py-1 border rounded text-xs hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center"
          >
            Next <ChevronRight className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setPage(totalPages - 1)}
            disabled={page >= totalPages - 1 || loading}
            className="px-2 py-1 border rounded text-xs hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            ››
          </button>
        </div>
      </div>
    </div>
  );
}

function CreateRowForm({ siteId, onCreated, onCancel }) {
  const { showError, showSuccess } = useAlert();
  const [form, setForm] = useState({
    timestamp: new Date().toISOString().slice(0, 16),
    activity_type: 'electricity_consumption',
    activity_value: '',
    unit: 'kWh',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!siteId) {
      showError('Select a site first');
      return;
    }
    if (!form.activity_value) {
      showError('Activity value is required');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/mrv/activity-data`, {
        site_id: siteId,
        timestamp: new Date(form.timestamp).toISOString(),
        activity_type: form.activity_type,
        activity_value: parseFloat(form.activity_value),
        unit: form.unit,
        notes: form.notes || null,
      });
      showSuccess('Activity row added');
      onCreated();
    } catch (e) {
      showError(e.response?.data?.detail || 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mb-4 p-4 border border-emerald-200 bg-emerald-50 rounded-md">
      <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        <input type="datetime-local" value={form.timestamp}
               onChange={e => setForm(f => ({ ...f, timestamp: e.target.value }))}
               className="px-2 py-1 text-sm border rounded" />
        <select value={form.activity_type}
                onChange={e => setForm(f => ({ ...f, activity_type: e.target.value }))}
                className="px-2 py-1 text-sm border rounded">
          {ACTIVITY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <input type="number" step="0.01" placeholder="Value"
               value={form.activity_value}
               onChange={e => setForm(f => ({ ...f, activity_value: e.target.value }))}
               className="px-2 py-1 text-sm border rounded" />
        <input type="text" placeholder="Unit"
               value={form.unit}
               onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}
               className="px-2 py-1 text-sm border rounded" />
        <input type="text" placeholder="Notes"
               value={form.notes}
               onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
               className="px-2 py-1 text-sm border rounded" />
      </div>
      <div className="mt-2 flex gap-2 justify-end">
        <button onClick={onCancel} className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
        <button onClick={submit} disabled={saving}
                className="px-3 py-1 bg-emerald-600 text-white rounded text-sm font-medium disabled:bg-gray-300">
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}
